import { ClubAdminAction } from '../club-ki-assistant/types';
import { normalizeClubAdminAction } from '../club-ki-assistant/utils';
import {
    APP_SUPPORT_DATA_VIEW_TYPES,
    AppSupportChatResponse,
    AppSupportDataView,
    AppSupportKnowledgeItem,
    AppSupportKnowledgeRoute,
    AppSupportView
} from './types';

const replaceClubSlugPlaceholder = (value: string | undefined, clubSlug: string) =>
    typeof value === 'string' ? value.replace(/\[slug\]/g, clubSlug) : value;

const normalizeKnowledgeRoute = (input: unknown): AppSupportKnowledgeRoute | null => {
    if (!input || typeof input !== 'object') return null;

    const value = input as Record<string, unknown>;
    if (typeof value.route !== 'string' || !value.route.trim()) return null;

    return {
        route: value.route,
        routeTitle: typeof value.route_title === 'string' ? value.route_title : undefined
    };
};

const applyClubSlugToKnowledgeRoute = (route: AppSupportKnowledgeRoute, clubSlug: string): AppSupportKnowledgeRoute => ({
    ...route,
    route: replaceClubSlugPlaceholder(route.route, clubSlug) || route.route,
    routeTitle: replaceClubSlugPlaceholder(route.routeTitle, clubSlug)
});

const normalizeKnowledgeItem = (input: unknown): AppSupportKnowledgeItem | null => {
    if (!input || typeof input !== 'object') return null;

    const value = input as Record<string, unknown>;

    return {
        contents: typeof value.contents === 'string' ? value.contents : undefined,
        modul: typeof value.modul === 'string' ? value.modul : undefined,
        routen: Array.isArray(value.routen)
            ? value.routen
                  .map((route) => normalizeKnowledgeRoute(route))
                  .filter((route): route is AppSupportKnowledgeRoute => route !== null)
            : [],
        title: typeof value.title === 'string' ? value.title : undefined
    };
};

const applyClubSlugToKnowledgeItem = (item: AppSupportKnowledgeItem, clubSlug: string): AppSupportKnowledgeItem => ({
    ...item,
    contents: replaceClubSlugPlaceholder(item.contents, clubSlug),
    modul: replaceClubSlugPlaceholder(item.modul, clubSlug),
    title: replaceClubSlugPlaceholder(item.title, clubSlug),
    routen: (item.routen || []).map((route) => applyClubSlugToKnowledgeRoute(route, clubSlug))
});

export const normalizeAppSupportView = (input: unknown): AppSupportView | null => {
    if (!input || typeof input !== 'object') return null;

    const value = input as Record<string, unknown>;

    if (value.type === 'knowledge') {
        return {
            type: 'knowledge',
            items: Array.isArray(value.items)
                ? value.items
                      .map((item) => normalizeKnowledgeItem(item))
                      .filter((item): item is AppSupportKnowledgeItem => item !== null)
                : []
        };
    }

    if (APP_SUPPORT_DATA_VIEW_TYPES.includes(value.type as AppSupportDataView['type'])) {
        return {
            type: value.type as AppSupportDataView['type'],
            items: Array.isArray(value.items)
                ? value.items.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
                : []
        };
    }

    return null;
};

const applyClubSlugToView = (view: AppSupportView, clubSlug: string): AppSupportView => {
    if (view.type !== 'knowledge') return view;

    return {
        ...view,
        items: (view.items || []).map((item) => applyClubSlugToKnowledgeItem(item, clubSlug))
    };
};

const normalizeViews = (input: unknown): AppSupportView[] => {
    if (!Array.isArray(input)) return [];

    return input
        .map((item) => normalizeAppSupportView(item))
        .filter((view): view is AppSupportView => view !== null);
};

const normalizeActions = (input: unknown): ClubAdminAction[] => {
    if (!Array.isArray(input)) return [];

    return input
        .map((item) => normalizeClubAdminAction(item))
        .filter((action): action is ClubAdminAction => action !== null);
};

export const normalizeAppSupportChatResponse = (data: AppSupportChatResponse, fallbackText: string, clubSlug: string) => {
    const singleView = normalizeAppSupportView(data.view);
    const views = (singleView ? [singleView] : normalizeViews(data.views)).map((view) =>
        applyClubSlugToView(view, clubSlug)
    );

    return {
        routeTitle: replaceClubSlugPlaceholder(typeof data.route_title === 'string' ? data.route_title : undefined, clubSlug) || null,
        suggestedRoute:
            replaceClubSlugPlaceholder(typeof data.suggested_route === 'string' ? data.suggested_route : undefined, clubSlug) || null,
        text: replaceClubSlugPlaceholder(data.answer || data.message || data.error || fallbackText, clubSlug)?.trim() || fallbackText,
        views,
        actions: normalizeActions(data.actions)
    };
};

export const resolveClubRoute = (route: string, clubSlug: string) => replaceClubSlugPlaceholder(route, clubSlug) ?? route;
